import tinycolor from "tinycolor2";
import Papa from "papaparse";
import jsSHA from "jssha";
import sanitize from "sanitize-filename";

import { serverUrl } from "js/config";
import { _ } from "js/vendor";
import Analytics from "js/analytics";
import perf from "./perf";
import getLogger from "js/core/logger";

const logger = getLogger();

export function getValueOrMixed(objs, propertyName) {
    let values = _.uniq(objs.filter(Boolean).map(o => o[propertyName]));
    if (values.length == 1) {
        return values[0];
    } else {
        return "mixed";
    }
}

/**
 * This function does not mutate the original array (but it preserves the references to the items if they're not scalar).
 * Reorders elements in an array by moving specified elements to a target index.
 *
 * @param {Array} array - The original array to reorder.
 * @param {Array<number>} indicesToMove - The indices of elements in the array to move.
 * @param {number} targetIndex - The index in the array where the elements should be moved to.
 * @returns {Array} A new array with the specified elements moved to the target index.
 */
export function reorderArray(array, indicesToMove, targetIndex) {
    // Extract the elements to be moved
    const elementsToMove = array.filter((_, index) => indicesToMove.includes(index));

    // Adjust the target index based on the positions of the elements to be moved
    if (Math.min(...indicesToMove) < targetIndex) {
        targetIndex += 1;
    } else {
        targetIndex -= indicesToMove.filter(index => index < targetIndex).length;
    }

    // Split the array into left and right parts excluding the elements to be moved
    const leftPart = array.filter((_, index) => index < targetIndex && !indicesToMove.includes(index));
    const rightPart = array.filter((_, index) => index >= targetIndex && !indicesToMove.includes(index));

    // Combine the left part, moved elements, and right part
    return [...leftPart, ...elementsToMove, ...rightPart];
}

/**
 * Calculates the median of an array of numbers.
 *
 * @param {number[]} numbers - The array of numbers to calculate the median for.
 * @returns {number} The median of the numbers.
 */
export function median(numbers) {
    if (!Array.isArray(numbers) || numbers.length === 0) {
        throw new Error("Input must be a non-empty array of numbers.");
    }

    const sortedNumbers = numbers.slice().sort((a, b) => a - b);
    const middleIndex = Math.floor(sortedNumbers.length / 2);

    if (sortedNumbers.length % 2 === 0) {
        return (sortedNumbers[middleIndex - 1] + sortedNumbers[middleIndex]) / 2;
    } else {
        return sortedNumbers[middleIndex];
    }
}

export function pluralize(str, count, pluralStr) {
    if (count == 1) {
        if (str.contains("##")) {
            return str.replace("##", 1);
        } else {
            return `1 ${str}`;
        }
    } else {
        if (pluralStr?.contains("##")) {
            return pluralStr.replace("##", count);
        } else {
            return `${count} ${pluralStr ?? str + "s"}`;
        }
    }
}

export function getCode(length) {
    var result = "";
    var characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() *
            charactersLength));
    }
    return result.toUpperCase();
}

export function getQueryVariables(variable) {
    var query = window.location.search.substring(1);
    var vars = query.split("&");
    for (var i = 0; i < vars.length; i++) {
        var pair = vars[i].split("=");
        if (decodeURIComponent(pair[0]) == variable) {
            return decodeURIComponent(pair[1]);
        }
    }
}

export function objectToArray(obj) {
    if (obj) {
        return _.map(obj, o => o);
    } else {
        return null;
    }
}

export function arrayToObject(array) {
    let o = {};
    for (let i = 0; i < array.length; i++) {
        o[i] = array[i];
    }
    return o;
}

export function convertCSSValue(value) {
    if (typeof value == "number") return value;
    if (value == undefined || value == null || value == "") {
        return 0;
    }
    var val = parseInt(value.replace(/%|px/, ""));
    if (value.indexOf("%") == -1) {
        return val;
    } else {
        return val / 100;
    }
}

export function getSizingValue(value, max) {
    if (typeof value == "number") return value;
    if (value == undefined || value == null || value == "") {
        return max;
    }
    var val = parseInt(value.replace(/%|px/, ""));
    if (value.indexOf("%") == -1) {
        return val;
    } else {
        return val / 100 * max;
    }
}

export function getCustomProperties(styles) {
    if (!styles) return null;
    var props = styles.content;

    if (!props) return null;

    //trim quotes
    props = props.substr(1, props.length - 2);

    var customProps = {};

    _.each(props.split(";"), prop => {
        customProps[prop.split(":")[0]] = prop.split(":")[1];
    });

    return customProps;
}

export function getPadding($el) {
    var padding = {};

    padding.left = parseInt($el.css("padding-left")) || 0;
    padding.top = parseInt($el.css("padding-top")) || 0;
    padding.bottom = parseInt($el.css("padding-bottom")) || 0;
    padding.right = parseInt($el.css("padding-right")) || 0;

    return padding;
}

export function getMargins($el) {
    var margin = {};
    margin.left = parseInt($el.css("margin-left")) || 0;
    margin.top = parseInt($el.css("margin-top")) || 0;
    margin.right = parseInt($el.css("margin-right")) || 0;
    margin.bottom = parseInt($el.css("margin-bottom")) || 0;
    return margin;
}

export function constrainValue(value, minValue, maxValue) {
    return Math.min(maxValue, Math.max(minValue, value));
}

export function calcTotalWidth(count, itemWidth, gap) {
    return count * (itemWidth + gap) - gap;
}

export function enumsToItemArray(enums, filter) {
    let items = [];
    if (filter) {
        for (let item of filter) {
            _.each(enums, (value, key) => {
                if (value == item) {
                    items.push({ value: value, label: _.startCase(key.toLowerCase()) });
                }
            });
        }
    } else {
        _.each(enums, (value, key) => {
            items.push({ value: value, label: _.startCase(key.toLowerCase()) });
        });
    }
    return items;
}

export function getShadowStyles(style) {
    // "rgba(0, 0, 0, 0.498039) 2px 2px 5px"

    var colorString = new RegExp("rgba(([^)]+))", "").exec(style)[1].substr(1);
    var rgba = colorString.split(",");

    var values = new RegExp("\\) (.*?)px (.*?)px (.*?)px", "g").exec("rgba(0, 0, 0, 0.498039) 2px 2px 5px");

    var shadow = {
        offsetX: parseInt(values[1]),
        offsetY: parseInt(values[2]),
        blur: parseInt(values[3]),
        rgb: [parseInt(rgba[0]), parseInt(rgba[1]), parseInt(rgba[2])],
        opacity: parseFloat(rgba[3])
    };

    return shadow;
}

export function elementsFromPoint(x, y) {
    var elements = [],
        previousPointerEvents = [],
        current,
        i,
        d;

    // get all elements via elementFromPoint, and remove them from hit-testing in order
    while ((current = document.elementFromPoint(x, y)) && elements.indexOf(current) === -1 && current != null) {
        // push the element and its current style
        elements.push(current);
        previousPointerEvents.push({
            value: current.style.getPropertyValue("pointer-events"),
            priority: current.style.getPropertyPriority("pointer-events")
        });

        // add "pointer-events: none", to get to the underlying element
        current.style.setProperty("pointer-events", "none", "important");
    }

    // restore the previous pointer-events values
    for (i = previousPointerEvents.length - 1; i >= 0; i--) {
        d = previousPointerEvents[i];
        elements[i].style.setProperty("pointer-events", d.value ? d.value : "", d.priority);
    }

    // return our results
    return elements;
}

export function blendColors(foreColor, backColor) {
    if (foreColor == null) return backColor;

    if (backColor.gradient) {
        // we can't blend colors with a gradient so return white/black so primary follows foregroundColor rules
        // https://beautifulai.atlassian.net/browse/BA-20999
        if (backColor.foregroundColor == "dark") {
            backColor = tinycolor("white");
        } else {
            backColor = tinycolor("black");
        }
    }

    if (typeof foreColor == "string") {
        foreColor = tinycolor(foreColor);
    }
    if (typeof backColor == "string") {
        backColor = tinycolor(backColor);
    }

    // Fast and easy way to combine (additive mode) two RGBA colors with JavaScript.
    var mix = {};
    mix.a = 1 - (1 - foreColor._a) * (1 - backColor._a); // alpha
    mix.r = Math.round(foreColor._r * foreColor._a / mix.a + backColor._r * backColor._a * (1 - foreColor._a) / mix.a); // red
    mix.g = Math.round(foreColor._g * foreColor._a / mix.a + backColor._g * backColor._a * (1 - foreColor._a) / mix.a); // green
    mix.b = Math.round(foreColor._b * foreColor._a / mix.a + backColor._b * backColor._a * (1 - foreColor._a) / mix.a); // blue

    let color = tinycolor(mix);
    color.isBlended = true;
    color.foreColor = foreColor.toRgbString();
    color.backColor = backColor.toRgbString();
    color.name = foreColor.name;
    return color;
}

export function areColorsEqual(color1, color2, ignoreAlpha = false) {
    color1 = tinycolor(color1);
    color2 = tinycolor(color2);
    if (ignoreAlpha) {
        return color1._r == color2._r && color1._g == color2._g && color1._b == color2._b;
    } else {
        return color1._r == color2._r && color1._g == color2._g && color1._b == color2._b && color1._a == color2._a;
    }
}

/**
 * Tracks an event.
 * @param {string} category
 * @param {string} action
 * @param {string} [label="none"]
 * @param {number} [value=0]
 * @param {Object} [props={}]
 * @param {Object} [options={}]
 * @param {boolean} [options.audit=false]
 * @param {boolean} [options.skipAmplitude=false]
 */
export function trackActivity(category, action, label = "none", value = 0, props = {}, { audit = false, skipAmplitude = false } = {}) {
    Analytics.util.track({
        category,
        action,
        label,
        value,
        props,
        audit,
        skipAmplitude,
    });
}

export function trackActivityViaParams(params) {
    const result = {
        category: null,
        action: null,
        label: "none",
        value: 0,
        props: {},
        audit: null,
        skipAmplitude: null,
        ...params,
    };
    Analytics.util.track(result);
}

export function trackState(props) {
    Analytics.util.trackState(props);
}

export function parseCSV(string, delimiter = "") {
    return Papa.parse(string, { header: false, delimiter: delimiter }).data;
}

export function isValidURL(string) {
    let regEx = /^[a-z](?:[-a-z0-9\+\.])*:(?:\/\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD])*@)?(?:\[(?:(?:(?:[0-9a-f]{1,4}:){6}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|::(?:[0-9a-f]{1,4}:){5}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){4}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:[0-9a-f]{1,4}:[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){3}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,2}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:){2}(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,3}[0-9a-f]{1,4})?::[0-9a-f]{1,4}:(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,4}[0-9a-f]{1,4})?::(?:[0-9a-f]{1,4}:[0-9a-f]{1,4}|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3})|(?:(?:[0-9a-f]{1,4}:){0,5}[0-9a-f]{1,4})?::[0-9a-f]{1,4}|(?:(?:[0-9a-f]{1,4}:){0,6}[0-9a-f]{1,4})?::)|v[0-9a-f]+[-a-z0-9\._~!\$&'\(\)\*\+,;=:]+)\]|(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])(?:\.(?:[0-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5])){3}|(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD])*)(?::[0-9]*)?(?:\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD]))*)*|\/(?:(?:(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD]))+)(?:\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD]))*)*)?|(?:(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD]))+)(?:\/(?:(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD]))*)*|(?!(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD])))(?:\?(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\/\?\xA0-\uD7FF\uE000-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E\uDB80-\uDBBE\uDBC0-\uDBFE][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F\uDBBF\uDBFF][\uDC00-\uDFFD])*)?(?:\#(?:%[0-9a-f][0-9a-f]|[-a-z0-9\._~!\$&'\(\)\*\+,;=:@\/\?\xA0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]|[\uD800-\uD83E\uD840-\uD87E\uD880-\uD8BE\uD8C0-\uD8FE\uD900-\uD93E\uD940-\uD97E\uD980-\uD9BE\uD9C0-\uD9FE\uDA00-\uDA3E\uDA40-\uDA7E\uDA80-\uDABE\uDAC0-\uDAFE\uDB00-\uDB3E\uDB44-\uDB7E][\uDC00-\uDFFF]|[\uD83F\uD87F\uD8BF\uD8FF\uD93F\uD97F\uD9BF\uD9FF\uDA3F\uDA7F\uDABF\uDAFF\uDB3F\uDB7F][\uDC00-\uDFFD])*)?$/i;
    return regEx.test(string);
}

export function continuousBinarySearch(start, end, tolerance, fn) {
    let next = (start + end) / 2;
    if (end - start < tolerance) return next;
    return fn(next) ? continuousBinarySearch(start, next, tolerance, fn) : continuousBinarySearch(next, end, tolerance, fn);
}

export function findInsertionIndex(arr, item, low = 0, high = arr.length - 1) {
    if (low > high) {
        if (high === -1) {
            return 0; // item is smaller than all elements in the array
        } else if (low === arr.length) {
            return arr.length; // item is larger than all elements in the array
        } else {
            return low; // item should be inserted between arr[high] and arr[low]
        }
    }

    const mid = Math.floor((low + high) / 2);
    if (arr[mid] < item) {
        return findInsertionIndex(arr, item, mid + 1, high);
    } else if (arr[mid] > item) {
        return findInsertionIndex(arr, item, low, mid - 1);
    } else {
        return mid;
    }
}

export function prefixZeros(val, count) {
    const str = val.toString();
    const base = _.times(count, () => "0");
    return [...base.slice(str.length), ...str].join("");
}

// Like Promise.all, but only run `max` promises at once,
// and adds a timeout of `delay` after each promise.
//
// instead of promises, takes zero-argument functions which return
// promises.
export function staggerPromises(fns, max = 1, delay = 0) {
    if (fns.length === 0) return Promise.resolve([]);
    return new Promise((resolve, reject) => {
        let curr = 0;
        let finished = false;
        let results = new Array(fns.length);
        for (let ii = 0; ii < max; ii++) {
            shift();
        }

        function shift() {
            if (finished) return;
            if (curr == fns.length + max - 1) {
                finished = true;
                resolve(results);
            } else {
                let idx = curr++;
                if (idx >= fns.length) return;
                let next = fns[idx]();
                if (!(next.then)) {
                    // wrap plain values to be consistent with Promise.all
                    next = Promise.resolve(next);
                }
                next.then(result => {
                    results[idx] = result;
                    if (delay) {
                        setTimeout(shift, delay);
                    } else {
                        shift();
                    }
                }).catch(err => {
                    finished = true;
                    reject(err);
                });
            }
        }
    });
}

let downloadIframeLoadPromise = null;

function getDownloadIframeSrc() {
    if (!serverUrl) {
        return "/download";
    }

    try {
        const serverOrigin = new URL(serverUrl).origin;
        if (serverOrigin === window.location.origin) {
            return `${serverOrigin}/download`;
        }
    } catch {
        // Fall back to the current origin below.
    }

    return "/download";
}

// We run downloads in a hidden /download iframe instead of the main window.
// Some browsers treat a <a download> click as navigation and fire "beforeunload" event
// on the window that starts it, which will trigger our logic that is supposed to be only
// triggered on proper window unload/close which screws up some parts of our app.
// Keeping the click inside the iframe isolates that.
function getDownloadIframe() {
    if (!downloadIframeLoadPromise) {
        downloadIframeLoadPromise = new Promise((resolve, reject) => {
            const iframe = document.createElement("iframe");
            iframe.style.display = "none";
            iframe.src = getDownloadIframeSrc();
            iframe.onload = () => {
                try {
                    if (typeof iframe.contentWindow.download !== "function") {
                        reject(new Error("download method not found or not accessible in iframe content window"));
                    }
                    resolve(iframe);
                } catch (err) {
                    reject(err);
                }
            };
            document.body.appendChild(iframe);
        });
    }

    return downloadIframeLoadPromise;
}

function fallbackDownload(href, fileName) {
    const a = document.createElement("a");
    a.href = href;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
}

function fallbackDownloadFromBlob(blob, fileName, contentType = null) {
    if (contentType) {
        blob = new Blob([blob], { type: contentType });
    }
    const blobUrl = window.URL.createObjectURL(blob);
    fallbackDownload(blobUrl, fileName);
    window.URL.revokeObjectURL(blobUrl);
}

async function fallbackDownloadFromUrl(url, fileName, contentType = null) {
    const response = await fetch(url);
    const blob = await response.blob();
    fallbackDownloadFromBlob(blob, fileName, contentType);
}

export function filenameForExport({ name, assetType, slideNum = null, secondarySlideNum = null }) {
    return sanitize(`Beautiful.ai - ${name}` +
        `${slideNum ? ` - Slide ${slideNum}` : ""}` +
        `${slideNum && secondarySlideNum && (secondarySlideNum !== 0) ? `-${secondarySlideNum}` : ""}` +
        `.${assetType}`);
}

export function downloadFromUrl(url, fileName, contentType = null) {
    return getDownloadIframe()
        .then(iframe => iframe.contentWindow.downloadFromUrl(url, fileName, contentType))
        .catch(err => {
            logger.error(err, "downloadFromUrl() failed, will fallback");
            return fallbackDownloadFromUrl(url, fileName, contentType);
        });
}

export function downloadFromBlob(blob, fileName, contentType = null) {
    return getDownloadIframe()
        .then(iframe => iframe.contentWindow.downloadFromBlob(blob, fileName, contentType))
        .catch(err => {
            logger.error(err, "downloadFromBlob() failed, will fallback");
            return fallbackDownloadFromBlob(blob, fileName, contentType);
        });
}

export function download(href, fileName) {
    return getDownloadIframe()
        .then(iframe => iframe.contentWindow.download(href, fileName))
        .catch(err => {
            logger.error(err, "download() failed, will fallback");
            return fallbackDownload(href, fileName);
        });
}

export function createHash(data, type = "TEXT") {
    let shaObj = new jsSHA("SHA-256", type);
    shaObj.update(data);
    return shaObj.getHash("HEX");
}

export function calculateTimeRemaining(time, options) {
    return extractUnitsFromDuration(time - Date.now());
}

export function calculateTimeElapsed(time, options) {
    return extractUnitsFromDuration(Date.now() - time, options);
}

function extractUnitsFromDuration(duration, { allowNegatives, roundToSecond } = {}) {
    // rounding
    if (roundToSecond) {
        duration = (Math.ceil(duration / 1000) + 1) * 1000;
    }

    const expired = duration <= 0;
    let days = (Math.floor(duration / 1000 / 60 / 60 / 24));
    let hours = (Math.floor(duration / 1000 / 60 / 60)) % 24;
    let minutes = (Math.floor(duration / 1000 / 60)) % 60;
    let seconds = (Math.floor(duration / 1000)) % 60;
    let ms = Math.floor(duration % 1000);

    // zero out negative values
    if (!allowNegatives) {
        days = Math.max(days, 0);
        hours = Math.max(hours, 0);
        minutes = Math.max(minutes, 0);
        seconds = Math.max(seconds, 0);
        ms = Math.max(ms, 0);
    }

    return { days, hours, minutes, seconds, ms, expired };
}

export function blobToDataURL(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            resolve(reader.result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function toDataUrl(url) {
    if (!url) {
        return Promise.resolve(null);
    }
    return fetch(url)
        .then(resp => resp.blob())
        .then(blob => {
            return blobToDataURL(blob);
        });
}

export function rand(min, max) {
    return min + Math.round(Math.random() * (max - min));
}

export function getSymbolCmdOrCtrl() {
    return window.navigator.platform.includes("Mac") ? "⌘" : "Ctrl";
}

export function getSymbolOptOrAlt() {
    return window.navigator.platform.includes("Mac") ? "Opt" : "Alt";
}

export function calcEaseInOut(progress) {
    return 1 - (Math.cos(Math.PI * progress) + 1) / 2;
}

export function caseInsensitiveSort(array, getString = item => item) {
    if (!array) return [];

    const sorter = (a, b) => {
        const compareA = (getString(a) || "").toString().toLowerCase();
        const compareB = (getString(b) || "").toString().toLowerCase();
        return compareB < compareA ? 1 : -1;
    };

    // Avoid mutating the original array
    return [...array].sort(sorter);
}

export function cancelPropagation(event) {
    event.stopImmediatePropagation?.();
    event.stopPropagation?.();
}

export function cancelEvent(event) {
    cancelPropagation(event);
    event.preventDefault?.();
}

export function sortTagsByPopularity(tagGroups) {
    let tagWeights = tagGroups.reduce((weights, tags) => {
        tags = tags || [];

        // tags sometimes comes in as a comma-separated string, so we need to convert it to an array
        if (tags && !Array.isArray(tags)) tags = tags.split(",").map(tag => tag.trim());

        tags.forEach(tag => {
            let weight = weights[tag] || 0;
            weights[tag] = weight + 1;
        });

        return weights;
    }, {});

    let weightedTags = Object.entries(tagWeights)
        .sort(([tagA, weightA], [tagB, weightB]) => {
            let result = (
                weightA > weightB ? -1
                    : weightB > weightA ? 1
                        : tagA < tagB ? -1
                            : tagB < tagA ? 1
                                : 0
            );
            return result;
        });
    let sortedTags = weightedTags.map(([tag]) => tag);

    return sortedTags;
}

export const stopEventPropagation = {
    onMouseDown: event => event.stopPropagation(),
    onMouseUp: event => event.stopPropagation(),
    onMouseMove: event => event.stopPropagation(),
    onKeyDown: event => event.stopPropagation(),
};

export { perf };

window.utils = module.exports;
